"use client";

import { Link } from 'react-router';
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bomb,
  CircleDollarSign,
  Crosshair,
  Info,
  Map as MapIcon,
  Shield,
  Swords,
  Zap,
} from "lucide-react";
import { PageContainer } from "@/components/PageContainer";
import { PageLoading } from "@/components/PageLoading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useValorantMatchDetail,
  type ValorantMatchDetail,
} from "@/hooks/api/useValorantTracking";
import { useOrganization } from "@/lib/OrganizationContext";
import { UnifiedMatchOverview } from "../components/UnifiedMatchOverview";

type MapAsset = {
  displayName: string;
  displayIcon?: string;
  xMultiplier: number;
  yMultiplier: number;
  xScalarToAdd: number;
  yScalarToAdd: number;
};
type AgentAsset = { displayName: string; displayIcon?: string };

const PLAYER_COLORS = ["#38bdf8", "#a78bfa", "#f59e0b", "#14b8a6", "#f472b6"];
const eventLabel = (type: string) =>
  ({
    kill: "Eliminación",
    death: "Muerte",
    plant: "Plantación de Spike",
    defuse: "Desactivación de Spike",
  })[type] ?? type;
const roundResultLabel = (result?: string, planted?: boolean) => {
  if (!result) return planted ? "Spike detonada" : "Eliminación total";
  return (
    {
      elimination: "Eliminación total",
      bomb: "Spike detonada",
      bombdefuse: "Spike desactivada",
      surrender: "Rendición",
      roundtimer: "Tiempo agotado",
    }[result.toLowerCase()] ?? result
  );
};

const formatDuration = (seconds?: number | null) => {
  if (!seconds) return "—";
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export const ValorantMatchDetailPage = ({ matchId }: { matchId: string }) => {
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const detail = useValorantMatchDetail(currentOrganization?.id, matchId);
  const requestedTab = searchParams.get("tab");
  const tab = ["overview", "rounds", "map"].includes(requestedTab ?? "")
    ? requestedTab!
    : "overview";
  const requestedRound = Number(searchParams.get("round"));
  const round: number | "all" =
    Number.isInteger(requestedRound) && requestedRound > 0
      ? requestedRound
      : "all";
  const requestedEvent = searchParams.get("event");
  const eventType: "all" | "kill" | "death" | "plant" | "defuse" =
    requestedEvent === "kill" ||
    requestedEvent === "death" ||
    requestedEvent === "plant" ||
    requestedEvent === "defuse"
      ? requestedEvent
      : "all";
  const focusedPlayer = searchParams.get("focus") || "all";
  const updateNavigation = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (!value) next.delete(key);
        else next.set(key, value);
      });
      const query = next.toString();
      navigate(query ? `${pathname}?${query}` : pathname);
    },
    [pathname, navigate, searchParams],
  );
  const backParams = new URLSearchParams();
  ["player", "days", "map"].forEach((key) => {
    const value = searchParams.get(key);
    if (value) backParams.set(key, value);
  });
  const backHref = `/valorant/performance${backParams.size ? `?${backParams}` : ""}`;
  const mapAssets = useQuery({
    queryKey: ["valorant-map-assets"],
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const response = await fetch("https://valorant-api.com/v1/maps");
      if (!response.ok) throw new Error("No fue posible cargar el mapa");
      return (await response.json()) as { data: MapAsset[] };
    },
  });
  const agentAssets = useQuery({
    queryKey: ["valorant-agent-assets"],
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const response = await fetch(
        "https://valorant-api.com/v1/agents?isPlayableCharacter=true",
      );
      if (!response.ok) throw new Error("No fue posible cargar los agentes");
      return (await response.json()) as { data: AgentAsset[] };
    },
  });
  const data = detail.data;
  const mapAsset = useMemo(
    () =>
      mapAssets.data?.data.find(
        (item) =>
          item.displayName.toLowerCase() === data?.match.map.toLowerCase(),
      ),
    [data?.match.map, mapAssets.data?.data],
  );
  const events = useMemo(
    () =>
      (data?.events ?? []).filter(
        (event) =>
          (round === "all" || event.round_number === round) &&
          (eventType === "all" || event.event_type === eventType) &&
          (focusedPlayer === "all" || event.player_id === focusedPlayer),
      ),
    [data?.events, eventType, focusedPlayer, round],
  );
  const agentIcon = (agent: string) =>
    agentAssets.data?.data.find(
      (item) => item.displayName.toLowerCase() === agent.toLowerCase(),
    )?.displayIcon;
  const plotted = (x: number, y: number) => ({
    left: `${Math.max(0, Math.min(100, (y * (mapAsset?.xMultiplier ?? 0.00007) + (mapAsset?.xScalarToAdd ?? 0.5)) * 100))}%`,
    top: `${Math.max(0, Math.min(100, (x * (mapAsset?.yMultiplier ?? -0.00007) + (mapAsset?.yScalarToAdd ?? 0.5)) * 100))}%`,
  });

  if (detail.isLoading) {
    return (
      <PageContainer title="Análisis de partida">
        <PageLoading text="Reconstruyendo la partida…" />
      </PageContainer>
    );
  }
  if (!data || detail.isError) {
    return (
      <PageContainer title="Análisis de partida">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="font-semibold">No fue posible abrir esta partida</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to={backHref}>Volver a rendimiento</Link>
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const requestedPlayerId = searchParams.get("player");
  const tracked =
    data.scoreboard.find(
      (player) => player.player_id === requestedPlayerId,
    ) ?? data.scoreboard.find((player) => player.is_tracked);
  const ownTeam = tracked?.team;
  const teamRows = Array.from(
    new Set(data.scoreboard.map((player) => player.team)),
  );
  const opponentTeam = teamRows.find((team) => team !== ownTeam);
  const ownTeamData = ownTeam ? data.match.teams[ownTeam] : undefined;
  const opponentTeamData = opponentTeam
    ? data.match.teams[opponentTeam]
    : undefined;

  return (
    <PageContainer
      title="Análisis de partida"
      subtitle="Revisión táctica, rendimiento individual y secuencia por rondas"
    >
      <div className="space-y-5">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={backHref}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a rendimiento
          </Link>
        </Button>

        <section className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex flex-col gap-5 border-b border-border bg-muted/30 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-lg border border-border bg-background">
                {tracked && agentIcon(tracked.agent) ? (
                  <span
                    className="h-full w-full rounded-lg bg-contain bg-center bg-no-repeat"
                    style={{
                      backgroundImage: `url(${agentIcon(tracked.agent)})`,
                    }}
                    role="img"
                    aria-label={`Retrato de ${tracked.agent}`}
                  />
                ) : (
                  <MapIcon className="h-6 w-6 text-primary" />
                )}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-semibold">{data.match.map}</h2>
                  <Badge variant="outline">Competitivo</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {new Date(data.match.started_at).toLocaleString("es-CL")} ·{" "}
                  {formatDuration(data.match.duration_seconds)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Resultado
                </p>
                <p className="text-3xl font-semibold tabular-nums">
                  {ownTeamData?.rounds_won ?? "—"} —{" "}
                  {opponentTeamData?.rounds_won ?? "—"}
                </p>
              </div>
              <Badge
                className={
                  ownTeamData?.has_won ? "bg-emerald-600" : "bg-red-600"
                }
              >
                {ownTeamData?.has_won ? "Victoria" : "Derrota"}
              </Badge>
            </div>
          </div>
          {tracked && (
            <div className="grid divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:grid-cols-6">
              {[
                ["Agente", tracked.agent],
                [
                  "KDA",
                  `${tracked.kills}/${tracked.deaths}/${tracked.assists}`,
                ],
                ["ACS", tracked.acs],
                ["ADR", tracked.adr],
                ["Headshot", `${tracked.headshot_percent}%`],
                ["Rango", tracked.rank || "—"],
              ].map(([label, value]) => (
                <div key={label} className="p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 font-semibold tabular-nums">{value}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <Tabs
          value={tab}
          onValueChange={(value) =>
            updateNavigation({ tab: value === "overview" ? null : value })
          }
        >
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview">Resumen</TabsTrigger>
            <TabsTrigger value="rounds">Rondas</TabsTrigger>
            <TabsTrigger value="map">Mapa táctico</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="space-y-5 pt-3">
            {tracked && ownTeam && (
              <UnifiedMatchOverview
                focusPlayerId={tracked.player_id || tracked.name}
                ownTeam={ownTeam}
                rounds={data.match.rounds_played}
                players={data.scoreboard.map((player) => ({
                  id: player.player_id || player.name,
                  name: player.name,
                  team: player.team,
                  agent: player.agent,
                  agentIcon: agentIcon(player.agent),
                  rank: player.rank,
                  tracked: player.is_tracked,
                  kills: player.kills,
                  deaths: player.deaths,
                  assists: player.assists,
                  acs: player.acs,
                  adr: player.adr,
                  headshots: player.headshots ?? 0,
                  bodyshots: player.bodyshots ?? 0,
                  legshots: player.legshots ?? 0,
                  damageMade: player.damage_made,
                  damageReceived: player.damage_received,
                  economySpent: player.economy_spent ?? 0,
                  economyLoadoutValue: player.economy_loadout_value ?? 0,
                  abilityCasts: player.ability_casts ?? { c: 0, q: 0, e: 0, x: 0 },
                }))}
              />
            )}
            <div className="grid gap-4 lg:grid-cols-3">
              {data.insights.map((insight) => (
                <Card key={insight.player_id}>
                  <CardHeader>
                    <CardTitle>{insight.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <Metric
                      label="Opening +/-"
                      value={insight.opening_differential}
                    />
                    <Metric label="K/D" value={insight.kill_death} />
                    <Metric
                      label="Daño +/-"
                      value={insight.damage_differential}
                    />
                    <Metric
                      label="Daño / 1K créditos"
                      value={insight.economy_efficiency}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Info className="h-4 w-4 text-primary" />
                  Información de la partida
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <Metric label="Match ID" value={data.match.id} />
                <Metric label="Región" value={data.match.region.toUpperCase()} />
                <Metric label="Versión" value={data.match.game_version || "—"} />
                <Metric label="Season ID" value={data.match.season_id || "—"} />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="rounds" className="pt-3">
            <Card>
              <CardHeader>
                <CardTitle>Secuencia de rondas</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Resultado y aportación registrada de los jugadores
                  monitorizados
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {data.rounds.map((item) => (
                    <button
                      key={item.number}
                      onClick={() =>
                        updateNavigation({
                          tab: "map",
                          round: String(item.number),
                        })
                      }
                      className="rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-muted-foreground">
                          R{item.number.toString().padStart(2, "0")}
                        </span>
                        <span
                          className={`h-2 w-2 rounded-full ${item.winning_team === ownTeam ? "bg-emerald-500" : "bg-red-500"}`}
                        />
                      </div>
                      <p className="mt-2 text-sm font-semibold">
                        {roundResultLabel(item.result, item.planted)}
                      </p>
                      <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                        <span>{item.tracked_kills} K</span>
                        <span>{item.tracked_deaths} D</span>
                        {item.planted && <Bomb className="h-3.5 w-3.5" />}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="map" className="pt-3">
            <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
              <Card>
                <CardHeader>
                  <CardTitle>Mapa táctico · {data.match.map}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Posiciones reales registradas al producirse cada evento
                  </p>
                </CardHeader>
                <CardContent>
                  <div
                    className="relative mx-auto aspect-square max-w-[720px] overflow-hidden rounded-lg border border-border bg-muted"
                    style={{
                      backgroundImage: mapAsset?.displayIcon
                        ? `url(${mapAsset.displayIcon})`
                        : undefined,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    {events.map((event) => {
                      const playerIndex = data.insights.findIndex(
                        (player) => player.player_id === event.player_id,
                      );
                      const player = data.insights[playerIndex];
                      return (
                        <button
                          key={event.id}
                          title={`${player?.name ?? "Jugador"} · Ronda ${event.round_number} · ${eventLabel(event.event_type)}${event.weapon ? ` · ${event.weapon}` : ""}`}
                          className={`absolute grid h-5 w-5 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 text-[8px] font-bold text-white shadow-sm ${event.event_type === "kill" ? "bg-emerald-600" : event.event_type === "death" ? "bg-red-600" : event.event_type === "plant" ? "bg-amber-500" : "bg-sky-600"}`}
                          style={{
                            ...plotted(event.x, event.y),
                            borderColor:
                              PLAYER_COLORS[
                                Math.max(0, playerIndex) % PLAYER_COLORS.length
                              ],
                          }}
                        >
                          {player?.name.slice(0, 1).toUpperCase() ?? "?"}
                        </button>
                      );
                    })}
                    {!events.length && (
                      <div className="absolute inset-0 grid place-items-center bg-background/70 text-sm text-muted-foreground">
                        Sin eventos para este filtro
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Filtros tácticos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Jugador
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={
                          focusedPlayer === "all" ? "default" : "outline"
                        }
                        onClick={() => updateNavigation({ focus: null })}
                      >
                        Todos
                      </Button>
                      {data.insights.map((player, index) => (
                        <Button
                          key={player.player_id}
                          size="sm"
                          variant={
                            focusedPlayer === player.player_id
                              ? "default"
                              : "outline"
                          }
                          onClick={() =>
                            updateNavigation({ focus: player.player_id })
                          }
                        >
                          <span
                            className="mr-2 h-2 w-2 rounded-full"
                            style={{
                              backgroundColor:
                                PLAYER_COLORS[index % PLAYER_COLORS.length],
                            }}
                          />
                          {player.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Ronda
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={round === "all" ? "default" : "outline"}
                        onClick={() => updateNavigation({ round: null })}
                      >
                        Todas
                      </Button>
                      {data.rounds.map((item) => (
                        <Button
                          key={item.number}
                          size="sm"
                          variant={
                            round === item.number ? "default" : "outline"
                          }
                          onClick={() =>
                            updateNavigation({ round: String(item.number) })
                          }
                        >
                          {item.number}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Evento
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        ["all", "kill", "death", "plant", "defuse"] as const
                      ).map((value) => (
                        <Button
                          key={value}
                          size="sm"
                          variant={eventType === value ? "default" : "outline"}
                          onClick={() =>
                            updateNavigation({
                              event: value === "all" ? null : value,
                            })
                          }
                        >
                          {value === "all" ? "Todos" : eventLabel(value)}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-border pt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                      Eliminación
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      Muerte
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-amber-500" />
                      Plantación de Spike
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-sky-600" />
                      Desactivación de Spike
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
};

const Metric = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
  </div>
);

export const PlayerDeepDive = ({
  player,
  rounds,
}: {
  player: ValorantMatchDetail["scoreboard"][number];
  rounds: number;
}) => {
  const headshots = player.headshots ?? 0;
  const bodyshots = player.bodyshots ?? 0;
  const legshots = player.legshots ?? 0;
  const impacts = headshots + bodyshots + legshots;
  const casts = player.ability_casts ?? { c: 0, q: 0, e: 0, x: 0 };

  return (
    <section className="grid gap-4 xl:grid-cols-4">
      <Card className="xl:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Crosshair className="h-4 w-4 text-primary" />
            Precisión de {player.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 divide-x divide-border rounded-lg border border-border bg-muted/20">
            {[
              ["Cabeza", headshots, "text-amber-600"],
              ["Cuerpo", bodyshots, "text-foreground"],
              ["Piernas", legshots, "text-muted-foreground"],
            ].map(([label, value, color]) => (
              <div key={label} className="p-3 text-center">
                <p className={`text-xl font-semibold tabular-nums ${color}`}>{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>{impacts} impactos registrados</span>
            <span>{player.headshot_percent}% headshot</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CircleDollarSign className="h-4 w-4 text-primary" />
            Economía
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Créditos gastados</span>
            <span className="font-semibold tabular-nums">{(player.economy_spent ?? 0).toLocaleString("es-CL")}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Valor de loadout</span>
            <span className="font-semibold tabular-nums">{(player.economy_loadout_value ?? 0).toLocaleString("es-CL")}</span>
          </div>
          <div className="flex justify-between gap-3 border-t border-border pt-3">
            <span className="text-muted-foreground">Daño / ronda</span>
            <span className="font-semibold tabular-nums">{Math.round(player.damage_made / Math.max(1, rounds))}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-primary" />
            Habilidades
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-4 gap-2">
          {(["c", "q", "e", "x"] as const).map((key) => (
            <div key={key} className="rounded-md border border-border bg-muted/20 p-2 text-center">
              <p className="text-lg font-semibold tabular-nums">{casts[key]}</p>
              <p className="text-[10px] font-medium uppercase text-muted-foreground">{key}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
};

export const Scoreboard = ({
  team,
  ownTeam,
  rows,
  agentIcon,
}: {
  team: string;
  ownTeam?: string;
  rows: ValorantMatchDetail["scoreboard"];
  agentIcon: (agent: string) => string | undefined;
}) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-2">
        {team === ownTeam ? (
          <Shield className="h-5 w-5 text-primary" />
        ) : (
          <Swords className="h-5 w-5 text-muted-foreground" />
        )}
        <CardTitle>
          {team === ownTeam ? "Equipo aliado" : "Equipo rival"}
        </CardTitle>
      </div>
    </CardHeader>
    <CardContent className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
            <th className="pb-3 text-left font-medium">Jugador</th>
            <th className="pb-3 text-left font-medium">Agente</th>
            <th className="pb-3 text-right font-medium">K / D / A</th>
            <th className="pb-3 text-right font-medium">ACS</th>
            <th className="pb-3 text-right font-medium">ADR</th>
            <th className="pb-3 text-right font-medium">HS</th>
            <th className="pb-3 text-right font-medium">Daño +/-</th>
          </tr>
        </thead>
        <tbody>
          {rows
            .sort((a, b) => b.acs - a.acs)
            .map((player) => (
              <tr
                key={`${team}-${player.name}`}
                className="border-b border-border/60 last:border-0"
              >
                <td className="py-3">
                  <span className="font-semibold">{player.name}</span>
                  {player.is_tracked && (
                    <Badge className="ml-2" variant="outline">
                      Roster
                    </Badge>
                  )}
                </td>
                <td className="py-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-7 w-7 rounded-md border border-border bg-muted bg-contain bg-center bg-no-repeat"
                      style={{
                        backgroundImage: agentIcon(player.agent)
                          ? `url(${agentIcon(player.agent)})`
                          : undefined,
                      }}
                      role="img"
                      aria-label={`Retrato de ${player.agent}`}
                    />
                    {player.agent}
                  </div>
                </td>
                <td className="py-3 text-right font-mono">
                  {player.kills}/{player.deaths}/{player.assists}
                </td>
                <td className="py-3 text-right tabular-nums">{player.acs}</td>
                <td className="py-3 text-right tabular-nums">{player.adr}</td>
                <td className="py-3 text-right tabular-nums">
                  {player.headshot_percent}%
                </td>
                <td className="py-3 text-right tabular-nums">
                  {player.damage_made - player.damage_received}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);
